/* GrowthOS — Opportunity record page.
   Re-skinned to mirror the Lead/Contact/Account detail (`.gos-leadrec` shell): a
   back row, header card (initials avatar, name, Mail button, dots menu, chips), a
   horizontal tab bar and a read-only Overview + sticky Details panel. Tabs reuse
   the SHARED child components (Timeline, Notes, Attachments, Email/compose) — same
   as the other three modules. Tags use the real Opportunity tag APIs (the legacy
   page already dispatched fetchTagsAndAppendToModule/fetchEmailAndAppendToModule
   for 'Opportunity' but never rendered a Tags or Emails UI — both are wired up here
   for the first time, using the exact same generic module-parameterized APIs Lead/
   Contact/Account already use, not a new capability).
   No Call/WhatsApp buttons (Opportunity has no phone field) and no enrichment/report
   menu items (no such API exists for Opportunity, unlike Account) — the dots menu is
   just "Edit Opportunity". No Campaigns/Opportunities/Contacts tabs either: a single
   opportunity doesn't enroll in campaigns as a list (just the one `campaignSource`
   field, already shown in Overview) and obviously can't embed itself. */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchAttachmentsAndAppendToModule,
  fetchDataAndUpdateStore,
  fetchDetailAndAppendToModule,
  fetchEmailAndAppendToModule,
  fetchNotesAndAppendToModule,
  fetchTagsAndAppendToModule,
} from '../../redux/actions';
import { getOpportunityById, getTagsByModule } from '../../redux/selector';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import apiServices from '../../services/service';
import { formatUSCurrency } from '../../shared/constants';
import { DateFormatter } from '../../shared/controls/formatters';
import Loader from '../../shared/component/Loader';
import Notes from '../sharedChild/note/notes';
import Attachments from '../sharedChild/attachment/attachments';
import Email from '../sharedChild/email/emails';
import ComposeEmail from '../sharedChild/email/compose';
import Timeline from '../sharedChild/timeline';

/* ---------- GrowthOS record icons (stroke, 24×24) ---------- */
const ICONS = {
  grid: <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />,
  clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  note: <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5" />,
  deal: <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />,
  target: (<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>),
  paperclip: <path d="M21 8l-9 9a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8-8" />,
  campaigns: <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1ZM15 8a4 4 0 0 1 0 8M18 5a8 8 0 0 1 0 14" />,
  mail: <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7" />,
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  info: (<><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></>),
  briefcase: <path d="M3 7h18v13H3zM8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />,
  dots: <path d="M12 5h.01M12 12h.01M12 19h.01" />,
  chevL: <path d="M15 18l-6-6 6-6" />,
  edit: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  tag: (<><path d="M20.6 13.4 11 3.8a2 2 0 0 0-1.4-.6H4a1 1 0 0 0-1 1v5.6a2 2 0 0 0 .6 1.4l9.6 9.6a2 2 0 0 0 2.8 0l4.6-4.6a2 2 0 0 0 0-2.8z" /><path d="M7.5 7.5h.01" /></>),
  user: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  plus: <path d="M12 5v14M5 12h14" />,
  search: (<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>),
  check: <path d="M20 6 9 17l-5-5" />,
  calendar: <path d="M8 2v4M16 2v4M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />,
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name] || null}
  </svg>
);

const GOS_AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const gosInitials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const gosAvaColor = (name) =>
  GOS_AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GOS_AVA_COLORS.length];

const val = (v) => (v === 0 || v ? v : null);

const InfoCard = ({ title, icon, rows }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className="gos-lr-card-rows">
      {rows.map(([label, value], i) => (
        <div className="gos-lr-drow" key={i}>
          <div className="gos-lr-dlabel">{label}</div>
          <div className={'gos-lr-dval' + (val(value) ? '' : ' muted')}>{val(value) ?? '—'}</div>
        </div>
      ))}
    </div>
  </div>
);
const DetailRow = ({ icon, label, children }) => (
  <div className="gos-lr-drow">
    <div className="gos-lr-dlabel">
      <Ic name={icon} size={15} /> {label}
    </div>
    <div className="gos-lr-dval gos-lr-dval-right">{children ?? '—'}</div>
  </div>
);

/* ---------- Opportunity tags editor (add / remove / create) ---------- */
const TAG_TONES = ['blue', 'green', 'amber', 'purple', 'rose', 'gray', 'red'];
const toneFor = (name) =>
  TAG_TONES[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % TAG_TONES.length];
const tagName = (t) => (Array.isArray(t?.name) ? t.name.join(', ') : t?.name || '');

const OpportunityTagEditor = ({ opportunity }) => {
  const dispatch = useDispatch();
  const library = useSelector(getTagsByModule('opportunityList')) || [];
  const applied = opportunity?.tags || [];
  const moduleId = opportunity?.id;

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchDataAndUpdateStore({ key: 'tagsByModule', inputData: 'Opportunity', storeKey: 'opportunityList' }));
  }, [dispatch]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQ('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open]);

  const refreshTags = () => {
    dispatch(fetchTagsAndAppendToModule({ moduleName: 'Opportunity', id: moduleId }));
    dispatch(fetchDataAndUpdateStore({ key: 'tagsByModule', inputData: 'Opportunity', storeKey: 'opportunityList' }));
  };

  const addTagByName = async (name) => {
    if (!moduleId || busy) return;
    setBusy(true);
    try {
      const res = await apiServices.createTags({ tagName: [name], module: 'Opportunity', moduleId: [moduleId] });
      if (res.status === 200) {
        toast.success('Tag added successfully!', { position: 'top-right' });
        refreshTags();
      }
    } catch (err) {
      console.error('Error adding tag:', err);
      toast.error('Failed to add tag.', { position: 'top-right' });
    } finally {
      setBusy(false);
    }
  };

  const removeTag = async (tag) => {
    if (!moduleId || busy) return;
    setBusy(true);
    try {
      const res = await apiServices.deleteTagsByModuleId({ id: [tag.id], module: 'Opportunity', moduleId: [moduleId] });
      if (res.status === 200) {
        toast.success('Tag removed successfully!', { position: 'top-right' });
        refreshTags();
      }
    } catch (err) {
      console.error('Error removing tag:', err);
      toast.error('Failed to remove tag.', { position: 'top-right' });
    } finally {
      setBusy(false);
    }
  };

  const toggleLibraryTag = (libTag) => {
    const name = tagName(libTag);
    const existing = applied.find((a) => tagName(a) === name);
    if (existing) removeTag(existing);
    else addTagByName(name);
  };

  const ql = q.trim();
  const filtered = ql ? library.filter((t) => tagName(t).toLowerCase().includes(ql.toLowerCase())) : library;
  const exact = library.some((t) => tagName(t).toLowerCase() === ql.toLowerCase());
  const nextTone = TAG_TONES[library.length % TAG_TONES.length];

  const create = () => {
    if (!ql || exact || busy) return;
    addTagByName(ql);
    setQ('');
    inputRef.current?.focus();
  };

  return (
    <div className="gos-lead-tags">
      <span className="gos-lead-tags-label">
        <Ic name="tag" size={13} /> Tags
      </span>
      {applied.map((t) => {
        const tone = toneFor(tagName(t));
        return (
          <span
            key={t.id}
            className="gos-lead-tag"
            style={{ background: `var(--gos-${tone}-bg)`, color: `var(--gos-${tone}-fg)` }}
          >
            <span className="gos-lt-dot" />
            {tagName(t)}
            <button type="button" className="gos-lt-x" title="Remove tag" onClick={() => removeTag(t)} disabled={busy}>
              <Ic name="x" size={13} />
            </button>
          </span>
        );
      })}
      <div className="gos-tag-add-wrap" ref={wrapRef}>
        <button type="button" className={'gos-tag-add' + (open ? ' on' : '')} onClick={() => setOpen((o) => !o)}>
          <Ic name="plus" size={14} /> Add tag
        </button>
        {open ? (
          <div className="gos-tag-pop">
            <div className="gos-tag-pop-search">
              <Ic name="search" size={14} />
              <input
                ref={inputRef}
                placeholder="Search or create…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && ql && !exact) create();
                }}
              />
            </div>
            <div className="gos-tag-pop-label">{ql ? 'Matching tags' : 'Apply tags'}</div>
            <div className="gos-tag-pop-list">
              {filtered.length === 0 && !ql ? <div className="gos-tag-pop-empty">No tags yet</div> : null}
              {filtered.map((t) => {
                const name = tagName(t);
                const on = applied.some((a) => tagName(a) === name);
                const tone = toneFor(name);
                return (
                  <button
                    type="button"
                    key={t.id}
                    className={'gos-tag-opt' + (on ? ' on' : '')}
                    onClick={() => toggleLibraryTag(t)}
                    disabled={busy}
                  >
                    <span className="gos-to-dot" style={{ background: `var(--gos-${tone}-fg)` }} />
                    {name}
                    <span className="gos-to-check">{on ? <Ic name="check" size={12} /> : null}</span>
                  </button>
                );
              })}
              {ql && filtered.length === 0 ? (
                <div className="gos-tag-pop-empty">No matches — create it below</div>
              ) : null}
            </div>
            {ql && !exact ? (
              <button type="button" className="gos-tag-create" onClick={create} disabled={busy}>
                <span
                  className="gos-tc-swatch"
                  style={{ background: `var(--gos-${nextTone}-bg)`, border: `1px solid var(--gos-${nextTone}-fg)` }}
                />
                Create <strong>{`“${ql}”`}</strong>
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ViewDeal = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const stateData = useSelector((state) => state);
  const storeLoading = isLoadingByKey(stateData, 'opportunityList');
  const [loading, setLoading] = useState(storeLoading);

  const searchParams = new URLSearchParams(location.search);
  const opportunityID = searchParams.get('id');
  const [opportunityLocalData, setOpportunityLocalData] = useState(location.state || {});
  const opportunityStoreData = useSelector((state) =>
    getOpportunityById(state, location.state?.id || opportunityID)
  );

  useEffect(() => {
    setOpportunityLocalData((prev) => ({ ...prev, ...opportunityStoreData }));
  }, [opportunityStoreData]);

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prev) => ({ ...prev, [name]: selectedOption }));
  };

  const handleOpenEditForm = () => {
    navigate('/opportunity/editOpportunity', { state: { ...opportunityLocalData, type: 'view', from: 'view' } });
  };

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    const opportunityId = location.state?.id || opportunityID;
    if (opportunityId) {
      dispatch(fetchDetailAndAppendToModule({ moduleName: 'Opportunity', id: opportunityId, localDetail: location.state || null }));
      dispatch(fetchNotesAndAppendToModule({ moduleName: 'Opportunity', id: opportunityId }));
      dispatch(fetchAttachmentsAndAppendToModule({ moduleName: 'Opportunity', id: opportunityId }));
      dispatch(fetchEmailAndAppendToModule({ moduleName: 'Opportunity', id: opportunityId }));
      dispatch(fetchTagsAndAppendToModule({ moduleName: 'Opportunity', id: opportunityId }));
    }
  }, [location, opportunityLocalData?.id]);

  const od = opportunityLocalData || {};
  const name = od.name || 'Opportunity';
  const amount = od.amount != null ? formatUSCurrency(od.amount) : null;
  const closingDate = od.closingDate ? DateFormatter(od.closingDate) : null;
  const chips = [
    od.stage && { icon: 'target', label: od.stage },
    od.account && { icon: 'building', label: od.account },
    od.projectType && { icon: 'deal', label: od.projectType },
    od.leadSource && { icon: 'campaigns', label: od.leadSource },
  ].filter(Boolean);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'grid' },
    { id: 'activities', label: 'Activities', icon: 'clock' },
    { id: 'notes', label: 'Notes', icon: 'note', count: od.notesList?.length },
    { id: 'attachments', label: 'Attachments', icon: 'paperclip', count: od.attachments?.length },
    { id: 'emails', label: 'Emails', icon: 'mail', count: od.emails?.length },
  ];

  const overview = (
    <div className="gos-lr-grid2">
      <InfoCard
        title="Deal Information"
        icon="deal"
        rows={[
          ['Opportunity Name', val(name)],
          ['Account', val(od.account)],
          ['Contact', val(od.contact)],
          ['Stage', val(od.stage)],
          ['Amount', val(amount)],
          ['Probability', od.probability != null ? `${od.probability}%` : null],
          ['Closing Date', val(closingDate)],
          ['Expected Revenue', val(od.expectedRevenue != null ? formatUSCurrency(od.expectedRevenue) : null)],
        ]}
      />
      <InfoCard
        title="Pipeline & Source"
        icon="target"
        rows={[
          ['Project Type', val(od.projectType)],
          ['Next Step', val(od.nextStep)],
          ['Forecast Category', val(od.forecastCategory)],
          ['Campaign Source', val(od.campaignSource)],
          ['Reach Out for Marketing', od.reachOutForMarketing === true ? 'Yes' : od.reachOutForMarketing === false ? 'No' : null],
        ]}
      />
      <InfoCard
        title="Ownership & Classification"
        icon="user"
        rows={[
          ['Owner', val(od.owner)],
          ['Industry', val(od.industry)],
          ['Business Type', val(od.businessType)],
          ['Lead Source', val(od.leadSource)],
          ['Source Name', val(od.sourceName)],
          ['Other Source Name', val(od.otherSourceName)],
          ['Line Of Business', val(od.lineOfBusiness)],
        ]}
      />
      <InfoCard
        title="Opportunity Details"
        icon="note"
        rows={[
          ['Description', val(od.description)],
          ['Technology Stack', val(od.technologyStack)],
        ]}
      />
    </div>
  );

  const detailsPanel = (
    <div className="gos-lr-card gos-lr-sticky">
      <div className="gos-lr-card-head">
        <div className="gos-lr-card-title">
          <Ic name="info" size={16} /> Details
        </div>
        <button type="button" className="gos-lr-iconbtn" onClick={handleOpenEditForm} title="Edit opportunity">
          <Ic name="edit" size={14} />
        </button>
      </div>
      <div className="gos-lr-sec">
        <div className="gos-lr-sec-title">Deal</div>
        <div className="gos-lr-card-rows">
          <DetailRow icon="deal" label="Name">{val(name)}</DetailRow>
          <DetailRow icon="target" label="Stage">{val(od.stage)}</DetailRow>
          <DetailRow icon="briefcase" label="Amount">{val(amount)}</DetailRow>
          <DetailRow icon="calendar" label="Closing Date">{val(closingDate)}</DetailRow>
          <DetailRow icon="user" label="Owner">{val(od.owner)}</DetailRow>
        </div>
      </div>
      <div className="gos-lr-divider" />
      <div className="gos-lr-sec">
        <div className="gos-lr-sec-title">Classification</div>
        <div className="gos-lr-card-rows">
          <DetailRow icon="building" label="Account">{val(od.account)}</DetailRow>
          <DetailRow icon="user" label="Contact">{val(od.contact)}</DetailRow>
          <DetailRow icon="briefcase" label="Industry">{val(od.industry)}</DetailRow>
          <DetailRow icon="campaigns" label="Lead Source">{val(od.leadSource)}</DetailRow>
          <DetailRow icon="note" label="Source Name">{val(od.sourceName)}</DetailRow>
        </div>
      </div>
    </div>
  );

  let tabBody;
  if (activeTab === 'overview') tabBody = overview;
  else if (activeTab === 'activities') tabBody = <Timeline localData={opportunityLocalData} module={'Opportunity'} />;
  else if (activeTab === 'notes') tabBody = <Notes leadLocalData={opportunityLocalData} module={'Opportunity'} />;
  else if (activeTab === 'attachments') tabBody = <Attachments localData={opportunityLocalData} module={'Opportunity'} />;
  else if (activeTab === 'emails') tabBody = <Email localData={opportunityLocalData} module={'Opportunity'} />;

  return (
    <>
      {loading && <Loader />}

      <div className="ViewLeadPageOuter gos-leadrec">
        {/* back row */}
        <div className="gos-lr-backrow">
          <button type="button" className="gos-lr-ghost-btn" onClick={() => navigate('/opportunity')}>
            <Ic name="chevL" size={15} /> Opportunities list
          </button>
          {od.id ? (
            <div className="gos-lr-leadid">
              Opportunity <span>{od.id}</span>
            </div>
          ) : null}
        </div>

        {/* header card */}
        <div className="gos-lr-card gos-lr-header">
          <div className="gos-lr-header-top">
            <div className="gos-lr-header-l">
              <span className="gos-lr-ava" style={{ background: gosAvaColor(name) }}>
                {gosInitials(name)}
              </span>
              <div className="gos-lr-header-meta">
                <div className="gos-lr-name">{name}</div>
                <div className="gos-lr-subline">
                  <Ic name="building" size={14} />
                  {[od.account, od.stage].filter(Boolean).join(' · ') || '—'}
                  {amount ? (
                    <>
                      <span className="gos-lr-dot">·</span>
                      <Ic name="deal" size={14} />
                      {amount}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="gos-lr-actions">
              <button type="button" className="gos-lr-contact-btn" onClick={() => setEmailOpen(true)}>
                <Ic name="mail" size={16} /> Mail
              </button>
              <button
                type="button"
                className="gos-lr-iconbtn gos-lr-iconbtn-bordered"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="More actions"
              >
                <Ic name="dots" size={18} />
              </button>
              {menuOpen ? (
                <>
                  <div className="gos-lr-menu-backdrop" onClick={() => setMenuOpen(false)} />
                  <div className="gos-lr-menu">
                    <button type="button" className="gos-lr-menu-item" onClick={() => { setMenuOpen(false); handleOpenEditForm(); }}>
                      <Ic name="edit" size={15} /> Edit Opportunity
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {chips.length ? (
            <div className="gos-lr-chips">
              {chips.map((c, i) => (
                <span className="gos-lr-chip" key={i}>
                  <Ic name={c.icon} size={14} /> {c.label}
                </span>
              ))}
            </div>
          ) : null}
          <OpportunityTagEditor opportunity={od} />
        </div>

        {/* tabs */}
        <div className="gos-lr-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={'gos-lr-tab' + (activeTab === t.id ? ' active' : '')}
              onClick={() => setActiveTab(t.id)}
            >
              <Ic name={t.icon} size={15} /> {t.label}
              {t.count != null ? <span className="gos-lr-count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {/* body */}
        {activeTab === 'overview' ? (
          overview
        ) : (
          <div className="gos-lr-2col">
            <div className="gos-lr-main">{tabBody}</div>
            {detailsPanel}
          </div>
        )}
      </div>

      {emailOpen && (
        <ComposeEmail
          localData={opportunityLocalData}
          module={'Opportunity'}
          handleClose={() => setEmailOpen(false)}
          handleChange={handleChange}
        />
      )}
    </>
  );
};

export default ViewDeal;
